import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, ArrowLeft } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { InvoiceMultiSelect } from '@/components/ui/invoice-multiselect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { paymentsService } from '@/services/paymentsService'
import { billsService } from '@/services/billsService'
import {
  PaymentVm,
  BillVendor,
  PaymentAccount,
  VendorBill,
  VendorAdvanceForPayment,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const PaymentsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const isViewMode = isEditMode
  const branchId = user?.selectedBranch?.branchId

  const [payment, setPayment] = useState<PaymentVm>({
    paymentId: 0,
    paymentDate: calendarToday(),
    paymentNo: '',
    amount: 0,
    vendorId: 0,
    advanceId: 0,
    refNo: '',
    paymentType: '',
    recipientAccount: 0,
    destinationAccount: 0,
    branchId: branchId || 0,
    note: '',
    ipAddress: '',
    billIds: [], // Now array of PaymentBillLine objects
  })

  // Additional form fields for display
  const [selectedAdvanceAmount, setSelectedAdvanceAmount] = useState<number>(0)
  const [, setPreviousPaymentAmount] = useState<number>(0)
  const [, setAdvanceLinesAmount] = useState<number>(0)
  
  // Bill table data - each row represents a bill with its payment details
  interface BillTableRow {
    billId: number
    billNo: string
    payStatus: string
    totalAmount: number
    paidAmount: number
    remainingAmount: number
    originalRemainingAmount: number
    amount: number
    lineId?: number
  }
  const [billTableData, setBillTableData] = useState<BillTableRow[]>([])
  const skipAllocate = useRef(isEditMode)

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

  const pickNum = (obj: any, ...keys: string[]) => {
    if (!obj) return 0
    for (const key of keys) {
      const raw = obj[key]
      if (raw !== undefined && raw !== null && raw !== '') {
        const n = Number(raw)
        if (!Number.isNaN(n)) return n
      }
    }
    return 0
  }

  const pickStr = (obj: any, ...keys: string[]) => {
    if (!obj) return ''
    for (const key of keys) {
      const raw = obj[key]
      if (raw !== undefined && raw !== null && String(raw) !== '') return String(raw)
    }
    return ''
  }

  const getAdvanceId = (adv: VendorAdvanceForPayment | undefined) =>
    Number(adv?.id ?? (adv as any)?.Id ?? adv?.advanceId ?? (adv as any)?.AdvanceId ?? 0)

  const getAdvanceAvailable = (adv: VendorAdvanceForPayment | undefined, extraUsedOnThisPayment = 0) => {
    if (!adv) return 0
    const total = Number(adv.amount ?? (adv as any).Amount ?? 0)
    const used = Number(adv.amountUsed ?? (adv as any).AmountUsed ?? 0)
    return Math.max(0, round2(total - used + extraUsedOnThisPayment))
  }

  const cashAfterAdvance = (lineTotal: number, advanceAvailable: number) =>
    Math.max(0, round2(lineTotal - Math.max(0, advanceAvailable)))

  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [bills, setBills] = useState<VendorBill[]>([])
  const [advances, setAdvances] = useState<VendorAdvanceForPayment[]>([])
  const [selectedVendor, setSelectedVendor] = useState<BillVendor | null>(null)
  const [selectedBillIds, setSelectedBillIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [allocationLocked, setAllocationLocked] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('payments.bank') },
    { value: 'Cash', label: t('payments.cash') },
  ]

  // Load vendors and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [vendorsData, accountsData] = await Promise.all([
          billsService.getAllVendors(branchId),
          paymentsService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load bills when vendor is selected
  useEffect(() => {
    if (!selectedVendor?.vendorId) {
      setBills([])
      setSelectedBillIds([])
      return
    }

    const loadBills = async () => {
      try {
        if (!selectedVendor?.vendorId) return
        const billsData = await paymentsService.getVendorsBills(selectedVendor.vendorId!)
        setBills(billsData || [])
      } catch (error: any) {
        console.error('Failed to load bills', error)
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadBills'))
      }
    }

    loadBills()
  }, [selectedVendor])

  useEffect(() => {
    if (skipAllocate.current) return

    if (selectedBillIds.length === 0) {
      setBillTableData([])
      return
    }

    setBillTableData((prev) =>
      selectedBillIds.map((idStr) => {
        const bill = bills.find((b) => String(pickNum(b, 'billId', 'BillId')) === idStr)
        const existing = prev.find((r) => String(r.billId) === idStr)
        const remainingFromBill = pickNum(bill, 'remainingAmount', 'RemainingAmount')
        const previouslyPaid = pickNum(bill, 'paidAmount', 'PaidAmount') || existing?.paidAmount || 0
        const totalAmount = pickNum(bill, 'total', 'Total') || existing?.totalAmount || 0
        const originalRemaining =
          existing && existing.originalRemainingAmount > 0 ? existing.originalRemainingAmount : remainingFromBill
        const typed = Math.min(existing?.amount || 0, Math.max(0, originalRemaining))
        return {
          billId: pickNum(bill, 'billId', 'BillId') || existing?.billId || Number(idStr),
          billNo: pickStr(bill, 'billNo', 'BillNo') || existing?.billNo || '',
          payStatus:
            originalRemaining === 0
              ? 'Paid'
              : previouslyPaid === 0
                ? 'Not Paid'
                : (pickStr(bill, 'payStatus', 'PayStatus') || existing?.payStatus || 'Partially Paid'),
          totalAmount,
          paidAmount: previouslyPaid,
          remainingAmount: round2(originalRemaining - typed),
          originalRemainingAmount: originalRemaining,
          amount: typed,
          lineId: existing?.lineId || 0,
        }
      })
    )
  }, [selectedBillIds, bills])

  useEffect(() => {
    if (isViewMode) return
    const totalLines = round2(billTableData.reduce((sum, row) => sum + (row.amount || 0), 0))
    const cash = cashAfterAdvance(totalLines, selectedAdvanceAmount)
    setPayment((prev) => (Math.abs((prev.amount || 0) - cash) < 0.001 ? prev : { ...prev, amount: cash }))
  }, [billTableData, selectedAdvanceAmount, isViewMode])

  // Load advances when vendor is selected
  useEffect(() => {
    if (!selectedVendor?.vendorId) {
      setAdvances([])
      return
    }

    const loadAdvances = async () => {
      try {
        if (!selectedVendor?.vendorId) return
        const paymentId = isEditMode && payment.paymentId ? payment.paymentId : 0
        const unused = await paymentsService.getVendorsAdvance(selectedVendor.vendorId, 0)
        if (paymentId && payment.advanceId) {
          const selected = await paymentsService.getVendorsAdvance(selectedVendor.vendorId, payment.advanceId)
          const merged = [...(unused || [])]
          for (const adv of selected || []) {
            const id = getAdvanceId(adv)
            if (!merged.some((a) => getAdvanceId(a) === id)) merged.push(adv)
          }
          setAdvances(merged)
        } else {
          setAdvances(unused || [])
        }
      } catch (error: any) {
        console.error('Failed to load advances', error)
        await showError('Error', error.response?.data?.message || t('payments.failedToLoadAdvances'))
      }
    }

    loadAdvances()
  }, [selectedVendor, isEditMode, payment.paymentId, payment.advanceId])

  // Load payment data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return
    if (vendors.length === 0) {
      // Wait for vendors to load
      return
    }

    const loadPayment = async () => {
      try {
        setLoading(true)
        skipAllocate.current = true
        const paymentId = Number(id)
        const paymentData = await paymentsService.getPaymentById(branchId, paymentId)

        const previousAmount = paymentData.amount || 0
        setPreviousPaymentAmount(previousAmount)

        let advanceLinesTotal = 0
        let advanceLines: any[] = []
        try {
          advanceLines = await paymentsService.getVendorAdvanceLinesByPaymentId(paymentId)
          advanceLinesTotal = advanceLines.reduce(
            (sum: number, line: any) => sum + Number(line.amount ?? line.Amount ?? 0),
            0
          )
          setAdvanceLinesAmount(advanceLinesTotal)
        } catch {
          setAdvanceLinesAmount(0)
        }

        const vendor = vendors.find(v => v.vendorId === paymentData.vendorId)
        if (vendor) {
          setSelectedVendor(vendor)
        }

        let paymentLines: any[] = []
        try {
          paymentLines = await paymentsService.getPaymentLinesByPaymentId(paymentId)
        } catch {
          paymentLines = []
        }

        setAllocationLocked(
          paymentLines.some((line: any) => Number(line.returnedAmount ?? line.ReturnedAmount ?? 0) > 0)
        )

        if (paymentData.vendorId) {
          const billsData = await paymentsService.getVendorsBills(paymentData.vendorId)
          setBills(billsData || [])

          const billIdsFromLines = paymentLines
            .map((line: any) => pickNum(line, 'billId', 'BillId'))
            .filter((billId: number) => billId > 0)

          if (billIdsFromLines.length > 0) {
            const selectedBillsList = (billsData || []).filter((bill) =>
              billIdsFromLines.includes(pickNum(bill, 'billId', 'BillId'))
            )
            setSelectedBillIds(billIdsFromLines.map((billId: number) => String(billId)))

            const tableData: BillTableRow[] = billIdsFromLines.map((billId: number) => {
              const bill =
                selectedBillsList.find((b) => pickNum(b, 'billId', 'BillId') === billId) ||
                ({ billId, billNo: `Bill ${billId}` } as VendorBill)
              const paymentLine = paymentLines.find((line: any) => pickNum(line, 'billId', 'BillId') === billId)
              const paymentLineAmount = pickNum(paymentLine, 'paidAmount', 'PaidAmount', 'amount')
              const lineId = pickNum(paymentLine, 'lineId', 'LineId')
              const currentPaid = pickNum(bill, 'paidAmount', 'PaidAmount')
              const currentRemaining = pickNum(bill, 'remainingAmount', 'RemainingAmount')
              const recalculatedPaidAmount = Math.max(0, currentPaid - paymentLineAmount)
              const recalculatedRemainingAmount = currentRemaining + paymentLineAmount

              return {
                billId,
                billNo: pickStr(bill, 'billNo', 'BillNo') || `Bill ${billId}`,
                payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                totalAmount: pickNum(bill, 'total', 'Total'),
                paidAmount: recalculatedPaidAmount,
                remainingAmount: recalculatedRemainingAmount,
                originalRemainingAmount: recalculatedRemainingAmount,
                amount: paymentLineAmount,
                lineId,
              }
            })
            setBillTableData(tableData)
          } else if (paymentData.billId) {
            const bill = (billsData || []).find(b => b.billId === paymentData.billId)
            if (bill) {
              setSelectedBillIds([String(bill.billId)])
              const recalculatedPaidAmount = (bill.paidAmount || 0) - previousAmount
              const recalculatedRemainingAmount = (bill.remainingAmount || 0) + previousAmount
              setBillTableData([{
                billId: bill.billId,
                billNo: bill.billNo || '',
                payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                totalAmount: Number(bill.total || 0),
                paidAmount: recalculatedPaidAmount > 0 ? recalculatedPaidAmount : 0,
                remainingAmount: recalculatedRemainingAmount,
                originalRemainingAmount: recalculatedRemainingAmount,
                amount: previousAmount,
              }])
            }
          }

          try {
            const unused = await paymentsService.getVendorsAdvance(paymentData.vendorId, 0)
            const selected = paymentData.advanceId
              ? await paymentsService.getVendorsAdvance(paymentData.vendorId, paymentData.advanceId)
              : []
            const merged = [...(unused || [])]
            for (const adv of selected || []) {
              const advId = getAdvanceId(adv)
              if (!merged.some((a) => getAdvanceId(a) === advId)) merged.push(adv)
            }
            setAdvances(merged)
            if (paymentData.advanceId) {
              const advance = merged.find((adv) => getAdvanceId(adv) === paymentData.advanceId)
              if (advance) {
                setSelectedAdvanceAmount(getAdvanceAvailable(advance, advanceLinesTotal))
              }
            }
          } catch {
            setAdvances([])
          }
        }

        setPayment({
          paymentId: paymentData.paymentId || 0,
          paymentDate: calendarFromApi(paymentData.paymentDate) || calendarToday(),
          paymentNo: paymentData.paymentNo || '',
          amount: paymentData.amount || 0,
          vendorId: paymentData.vendorId || 0,
          advanceId: paymentData.advanceId || 0,
          refNo: paymentData.refNo || '',
          paymentType: paymentData.paymentType || '',
          recipientAccount: paymentData.recipientAccount || 0,
          destinationAccount: paymentData.destinationAccount || 0,
          branchId: paymentData.branchId || branchId || 0,
          note: paymentData.note || '',
          ipAddress: paymentData.ipAddress || '',
          billIds: [],
        })
      } catch (error: any) {
        await showError('Error', error.message || t('payments.failedToLoadPayment'))
      } finally {
        setLoading(false)
      }
    }

    loadPayment()
  }, [isEditMode, branchId, id, vendors.length])

  // Handle vendor selection
  const handleVendorChange = (vendorId: string) => {
    const vendor = vendors.find(v => v.vendorId === Number(vendorId))
    if (vendor) {
      skipAllocate.current = false
      setSelectedVendor(vendor)
      setPayment(prev => ({
        ...prev,
        vendorId: vendor.vendorId || 0,
        billIds: [],
        advanceId: 0,
      }))
      setSelectedBillIds([])
      setBillTableData([])
      setSelectedAdvanceAmount(0)
    }
  }

  const handleBillChange = (billIds: string[]) => {
    skipAllocate.current = false
    setSelectedBillIds(billIds)
  }

  const handleAdvanceChange = (advanceId: string) => {
    skipAllocate.current = false
    if (!advanceId || advanceId === '') {
      setSelectedAdvanceAmount(0)
      setPayment(prev => ({
        ...prev,
        advanceId: 0,
      }))
      return
    }

    const advance = advances.find((adv) => String(getAdvanceId(adv)) === advanceId)
    if (advance) {
      setSelectedAdvanceAmount(getAdvanceAvailable(advance))
      setPayment((prev) => ({
        ...prev,
        advanceId: getAdvanceId(advance),
      }))
    }
  }

  const handleLinePaidChange = (billId: number, value: number) => {
    if (isViewMode) return
    setBillTableData((prev) =>
      prev.map((row) => {
        if (row.billId !== billId) return row
        const cap = round2(Math.max(0, row.originalRemainingAmount || 0))
        const paid = round2(Math.min(cap, Math.max(0, Number.isFinite(value) ? value : 0)))
        return {
          ...row,
          amount: paid,
          remainingAmount: round2(cap - paid),
        }
      })
    )
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (isViewMode) return
    if (!branchId) {
      await showValidationError(t('payments.pleaseSelectBranch'))
      return
    }

    if (selectedBillIds.length === 0) {
      await showValidationError(t('payments.pleaseSelectBill'))
      return
    }

    if (!payment.paymentType) {
      await showValidationError(t('payments.pleaseSelectPaymentType'))
      return
    }

    // Amount validation: If advance is selected, amount can be 0. Otherwise, amount must be > 0
    const hasAdvance = payment.advanceId && payment.advanceId > 0
    if (!hasAdvance && (!payment.amount || payment.amount <= 0)) {
      await showValidationError(t('payments.pleaseEnterValidAmount'))
      return
    }

    if (!payment.paymentDate) {
      await showValidationError(t('payments.pleaseSelectPaymentDate'))
      return
    }

    if (!payment.destinationAccount || payment.destinationAccount === 0) {
      await showValidationError(t('payments.selectDestinationAccount'))
      return
    }

    const totalAllocated = round2(billTableData.reduce((sum, row) => sum + (row.amount || 0), 0))
    const totalCap = round2(billTableData.reduce((sum, row) => sum + (row.originalRemainingAmount || 0), 0))
    const expectedCash = cashAfterAdvance(totalAllocated, selectedAdvanceAmount)

    if (totalAllocated <= 0) {
      await showValidationError(t('payments.pleaseEnterValidAmount'))
      return
    }
    if (totalAllocated > totalCap + 0.001) {
      await showValidationError(t('payments.amountCannotExceed', { amount: totalCap.toFixed(2) }))
      return
    }
    if (Math.abs((payment.amount || 0) - expectedCash) > 0.01) {
      setPayment((prev) => ({ ...prev, amount: expectedCash }))
    }

    // Declare paymentToSave outside try block for error logging
    let paymentToSave: PaymentVm | null = null

    try {
      setSaving(true)
      
      // Show loading
      showLoading('Saving Payment...', 'Please wait while we process your request')

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      // Convert date to ISO string format (YYYY-MM-DD -> ISO string)
      const dateISO = calendarToApi(payment.paymentDate)

      // Prepare billIds array with new structure
      const billIdsArray = billTableData
        .filter(row => (row.amount || 0) > 0)
        .map(row => ({
          lineId: row.lineId || 0,
          billId: row.billId,
          amount: row.amount || 0,
          isDeleted: 'false',
        }))

      // Prepare payload matching exact API structure
      paymentToSave = {
        paymentId: isEditMode ? (payment.paymentId || 0) : 0, // Always 0 for new payments
        paymentDate: dateISO, // ISO format
        paymentNo: payment.paymentNo || undefined, // Don't send empty string, let backend generate
        amount: expectedCash,
        vendorId: selectedVendor?.vendorId || 0,
        advanceId: payment.advanceId || 0,
        refNo: payment.refNo || '',
        paymentType: payment.paymentType,
        recipientAccount: 0,
        destinationAccount: payment.destinationAccount,
        branchId: branchId || 0,
        note: payment.note || '',
        ipAddress: ipAddress, // Set logged-in user's IP address
        billIds: billIdsArray, // New structure with lineId, billId, amount, isDeleted
      }

      // Enhanced console logging for better visibility
      console.group('🔵 PAYMENT SAVE PAYLOAD')
      console.log('Mode: ➕ INSERT')
      console.log('Endpoint:', 'POST /api/Payments/Insert')
      console.table(paymentToSave)
      console.log('Full Payload JSON:')
      console.log(JSON.stringify(paymentToSave, null, 2))
      console.log('Bill Lines Array:', paymentToSave.billIds)
      console.log('Total Amount:', paymentToSave.amount)
      console.log('Selected Vendor ID:', selectedVendor?.vendorId)
      console.groupEnd()

      await paymentsService.insertPayment(paymentToSave)

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(false, t('payments.paymentCreatedSuccess'))

      navigate('/purchases/payments')
    } catch (error: any) {
      // Enhanced error logging
      console.group('❌ PAYMENT SAVE ERROR')
      console.error('Error Type:', error.name)
      console.error('Error Message:', error.message)
      console.error('HTTP Status:', error.response?.status)
      console.error('Error Response Data:')
      console.error(error.response?.data)
      
      if (error.response?.data?.msg) {
        console.error('Backend Error Message:', error.response.data.msg)
      }
      
      if (error.response?.data?.errors) {
        console.error('Validation Errors:', error.response.data.errors)
      }
      
      if (paymentToSave) {
        console.error('Request Payload (that caused error):')
        console.error(JSON.stringify(paymentToSave, null, 2))
      } else {
        console.error('Request Payload: Could not reconstruct (error occurred before payload creation)')
      }
      console.groupEnd()
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, false)
    } finally {
      setSaving(false)
    }
  }

  const vendorOptions: SearchableSelectOption[] = vendors.map((vendor) => ({
    value: String(vendor.vendorId || 0),
    label: vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`,
  }))

  // Filter bills based on mode
  // In add new mode: exclude bills with remaining amount 0
  // In edit mode: only show already selected bills (regardless of remaining amount)
  // But if selectedBillIds is empty, show all bills (they will be filtered once selectedBillIds is set)
  const filteredBills = isEditMode
    ? (selectedBillIds.length > 0 
        ? bills.filter((bill) => selectedBillIds.includes(String(pickNum(bill, 'billId', 'BillId'))))
        : bills) // Show all bills if selectedBillIds not set yet
    : bills.filter((bill) => pickNum(bill, 'remainingAmount', 'RemainingAmount') > 0)

  const billOptions: SearchableSelectOption[] = filteredBills.map((bill) => ({
    value: String(pickNum(bill, 'billId', 'BillId') || 0),
    label: pickStr(bill, 'billNo', 'BillNo'),
    disabled: isEditMode, // Disable selection in edit mode
  }))

  const advanceOptions: SearchableSelectOption[] = advances
    .map((adv) => {
      const id = getAdvanceId(adv)
      const available = getAdvanceAvailable(adv)
      if (id <= 0 || available <= 0) return null
      return {
        value: String(id),
        label: `${adv.advNo || (adv as any).advanceToSupplierNo || (adv as any).AdvanceToSupplierNo || (adv as any).advanceNo || `Advance ${id}`} - Remaining: ${available.toFixed(2)}`,
      }
    })
    .filter((opt): opt is SearchableSelectOption => Boolean(opt))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payments.title')} description={isViewMode ? t('payments.viewPayment') : t('payments.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('payments.title')}
        description={isViewMode ? t('payments.viewPayment') : t('payments.createNew')}
        action={{
          label: t('payments.return'),
          onClick: () => navigate('/purchases/payments'),
          icon: ArrowLeft,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Vendor and Bill Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {t('payments.vendor')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={vendorOptions}
                    value={selectedVendor ? String(selectedVendor.vendorId) : ''}
                    onValueChange={handleVendorChange}
                    placeholder={t('payments.selectVendor')}
                    searchPlaceholder={t('payments.searchVendors')}
                    emptyMessage={t('payments.noVendorsFound')}
                    disabled={isViewMode || allocationLocked}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('payments.amount')}</Label>
                <div className="max-w-[400px]">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={payment.amount || 0}
                    readOnly
                    disabled
                    placeholder="0.00"
                    className="bg-muted cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('payments.amountCalculated')}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('payments.advance')}</Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={advanceOptions}
                    value={payment.advanceId ? String(payment.advanceId) : ''}
                    onValueChange={handleAdvanceChange}
                    placeholder={t('payments.selectAdvance')}
                    searchPlaceholder={t('payments.searchAdvances')}
                    emptyMessage={t('payments.noAdvancesFound')}
                    disabled={isViewMode || !selectedVendor || allocationLocked}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {t('payments.billRefNo')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <InvoiceMultiSelect
                    options={billOptions}
                    value={selectedBillIds}
                    onValueChange={isEditMode ? undefined : handleBillChange}
                    placeholder={t('payments.selectBill')}
                    searchPlaceholder={t('payments.searchBills')}
                    emptyMessage={t('payments.noBillsFound')}
                    disabled={isViewMode || !selectedVendor || allocationLocked}
                  />
                </div>
              </div>
            </div>

            {/* Bill Details Table */}
            {billTableData.length > 0 && (
              <div className="space-y-2">
                <Label>{t('payments.billDetails') || 'Bill Details'}</Label>
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('payments.billRefNo')}</TableHead>
                            <TableHead className="text-right">{t('payments.billTotal')}</TableHead>
                            <TableHead className="text-right">{t('payments.previouslyPaid')}</TableHead>
                            <TableHead className="text-right">{t('payments.paidAmount')}</TableHead>
                            <TableHead className="text-right">{t('payments.remainingAmount')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {billTableData.map((row) => {
                            const originalRemaining = row.originalRemainingAmount || row.remainingAmount
                            const currentRemaining = round2(originalRemaining - (row.amount || 0))

                            return (
                            <TableRow key={row.billId}>
                              <TableCell className="font-medium">{row.billNo}</TableCell>
                              <TableCell className="text-right">{Number(row.totalAmount || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">{Number(row.paidAmount || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">
                                {isViewMode ? (
                                  Number(row.amount || 0).toFixed(2)
                                ) : (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={row.amount || ''}
                                    onChange={(e) => handleLinePaidChange(row.billId, Number(e.target.value) || 0)}
                                    className="h-8 w-28 ml-auto text-right"
                                  />
                                )}
                              </TableCell>
                              <TableCell className="text-right">{currentRemaining >= 0 ? currentRemaining.toFixed(2) : '0.00'}</TableCell>
                            </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payment Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('payments.paymentInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('payments.paymentDate')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={payment.paymentDate}
                      onChange={(e) => setPayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.paymentNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={payment.paymentNo || ''}
                      onChange={(e) => setPayment(prev => ({ ...prev, paymentNo: e.target.value }))}
                      placeholder={t('payments.enterPaymentNumber')}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.destinationAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={payment.destinationAccount ? String(payment.destinationAccount) : ''}
                      onValueChange={(value) => setPayment(prev => ({ ...prev, destinationAccount: Number(value) }))}
                      placeholder={t('payments.selectDestinationAccount')}
                      searchPlaceholder={t('payments.searchAccounts')}
                      emptyMessage={t('payments.noAccountsFound')}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={payment.paymentType}
                      onValueChange={(value) => setPayment(prev => ({ ...prev, paymentType: value }))}
                      disabled={isViewMode}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('payments.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent searchable={false}>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('payments.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={payment.refNo || ''}
                      onChange={(e) => setPayment(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('payments.enterReferenceNumber')}
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('payments.note')}</Label>
              <Textarea
                value={payment.note || ''}
                onChange={(e) => setPayment(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('payments.enterNotes')}
                rows={4}
                disabled={isViewMode}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/purchases/payments')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        {!isViewMode && (
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('payments.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('payments.savePayment')}
            </>
          )}
        </Button>
        )}
      </div>
    </div>
  )
}

